import type { RadioGroupOptions } from '@latitude/radio-button';
import { RadioGroup } from '@latitude/radio-button';
import { FormComponentWrapper } from 'components/FormComponentWrapper';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { FormRadioButtonGroupProps, RadioButtonProps } from './types';

export const FormRadioButtonGroup = <
  OptionType extends {
    value: string;
    label?: React.ReactNode;
    disabled?: boolean;
  } = RadioGroupOptions,
  T extends FieldValues = FieldValues,
>({
  name,
  control,
  description,
  label,
  wrapperClassname,
  options,
  disabled,
  rules,
  rawProps: passedRawProps = {},
  render,
  className = 'flex flex-col gap-2',
  ...formRadioGroupProps
}: FormRadioButtonGroupProps<OptionType, T>) => {
  return (
    <Controller
      name={name}
      {...(control
        ? { control }
        : {
            // if undefined, Controller reads from FormProvider
          })}
      rules={rules}
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => {
        const { onBlur: passedOnBlur, ...rawProps } = passedRawProps ?? {
          onBlur: () => {},
        };
        const handleOnBlur = () => {
          onBlur();
          passedOnBlur?.({} as React.FocusEvent<HTMLInputElement>);
        };

        const handleOnChange = (str: string) => {
          onChange(str);
          handleOnBlur();
        };

        return (
          <FormComponentWrapper
            name={name}
            description={description}
            label={label}
            error={error}
            className={wrapperClassname}
            disabled={disabled}
          >
            {render ? (
              <RadioGroup
                rawProps={rawProps}
                onChange={handleOnChange}
                value={value}
                invalid={!!error}
                disabled={disabled}
                className={className}
                {...formRadioGroupProps}
              >
                {options.map((option, index) => {
                  const radioProps: RadioButtonProps = {
                    id: `radio-${name}-${index}`,
                    name,
                    value: option.value,
                    disabled: disabled || option.disabled,
                    invalid: !!error,
                    onBlur,
                    'data-testid': `radio-${name}-${index}`,
                    checked: value === option.value,
                    onValueChange: handleOnChange,
                  };

                  return (
                    <span key={`${option.value}-${index}`}>
                      {render(option, radioProps)}
                    </span>
                  );
                })}
              </RadioGroup>
            ) : (
              <RadioGroup
                rawProps={rawProps}
                onChange={handleOnChange}
                value={value}
                invalid={!!error}
                disabled={disabled}
                options={options as unknown as RadioGroupOptions[]}
                className={className}
                {...formRadioGroupProps}
              />
            )}
          </FormComponentWrapper>
        );
      }}
    />
  );
};
